// src/components/news/NewsCard.tsx
import { Card, CardContent, CardMedia, Box, Typography, Chip, Button } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { NewsArticle } from '@/types/news.types';
import { useState, useEffect } from 'react';
import { getImageUrl } from '@/utils/imageHelper';
import { formatDate } from '@/utils/formatDate';

interface NewsCardProps {
  newsItem: NewsArticle;
  variant?: 'default' | 'compact' | 'modal';
  onReadMore?: (imageUrl: string) => void;
}

export const NewsCard = ({ newsItem, variant = 'default', onReadMore }: NewsCardProps) => {
  const [imageUrl, setImageUrl] = useState<string>('/images/Logo.jpg');

  useEffect(() => {
    const loadImage = async () => {
      if (newsItem.imageName) {
        try {
          const url = await getImageUrl(newsItem.imageName);
          if (url) {
            setImageUrl(url);
          }
        } catch (error) {
          console.error('Error loading image:', error);
          setImageUrl('/images/Logo.jpg');
        }
      }
    };

    loadImage();
  }, [newsItem.imageName]);

  if (variant === 'modal') {
    return (
      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: { xs: '90px', sm: "100px", md: "120px" },
            height: "100%",
            objectFit: 'cover',
            alignItems: 'center',
            justifyItems: 'center',
            mt: 4,
            p: 1,
          }}
          image={imageUrl}
          alt={newsItem.title}
        />
        <CardContent sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
          <Chip
            label={newsItem.tag}
            size="small"
            sx={{
              mb: 1.5,
              backgroundColor: '#E8F5F3',
              color: '#00695C',
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              alignSelf: 'flex-start'
            }}
          />
          <Typography
            variant="h6"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              color: '#1F2937',
              fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem' }
            }}
          >
            {newsItem.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6b7280',
              mb: 2,
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {newsItem.summary}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                {formatDate(newsItem.date)}
              </Typography>
            </Box>
            {onReadMore && (
              <Button size="medium" onClick={() => onReadMore(imageUrl)} sx= {{
            backgroundColor: 'primary.main',
             color : '#fff',
             padding: '8px 16px',
             borderRadius: 2,
             textTransform: 'none',
             cursor: 'pointer',
             width: 'fit-content',
             '&:hover': {
               backgroundColor: 'primary.dark',
               transform: 'translateY(-2px)',
               boxShadow: '0 4px 12px rgba(107, 255, 193, 0.3)',
             }
            }}>
                Leer más
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Variant default - para la lista principal
  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        minHeight: { sm: 200 },
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)',
        }
      }}
    >
      <Box
        component="img"
        src={imageUrl}
        alt={newsItem.title}
        sx={{
          width: { xs: '90px', sm: "100px", md: "120px" },
          height: "100%",
          objectFit: 'cover',
          alignItems: 'center',
          justifyItems: 'center',
          mt: 4,
          pl: 4,
        }}
      />
      <CardContent sx={{ display: 'flex', padding: 4, flex: 1, height: '100%', flexDirection: 'column' }}>
        <Chip
          label={newsItem.tag}
          size="medium"
          sx={{ 
            marginBottom: 3, 
            backgroundColor: 'primary.100', 
            color: 'primary.dark', 
            alignSelf: 'flex-start' 
          }}
        />
        <Typography variant="h6" sx={{ marginBottom: 2, fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem', lg: '1.188rem' } }}>
          {newsItem.title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', marginBottom: 1, lineHeight: { lg: 1.6, md: 1.5 } }}>
          {newsItem.summary}
        </Typography>
        <Box sx={{
          marginTop: 'auto',
          display: 'flex',
          flexDirection: { lg: 'row', xs: 'column' },
          paddingBottom: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: { xs: 1.5, lg: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#6b7280', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {formatDate(newsItem.date)}
            </Typography>
          </Box>
          <Button
            size="medium"
            onClick={() => onReadMore && onReadMore(imageUrl)}
            sx={{
              backgroundColor: 'secondary.main',
              color: '#fff',
              padding: { xs: '8px 16px', sm: '6px 12px' },
              borderRadius: 2,
              textTransform: 'none',
              cursor: 'pointer',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(107, 255, 193, 0.3)',
              }
            }}
          >
            Leer más...
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};